import { useState } from "react";
import { track } from "@/lib/track";
import { APIProvider, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import MapLoadGuard, { useMapRetryKey } from "@/components/MapLoadGuard";
import { Button } from "@/components/ui/button";
import { Navigation } from "lucide-react";
import { inlineMapGestures } from "@/lib/mapGestures";

interface PropertyMapProps {
  /** Tuple in GeoJSON [lng, lat] order, matches the existing
   *  Property.coordinates shape so consumers don't change. */
  coordinates: [number, number];
  title: string;
  className?: string;
}

// Read-only Google Maps view of a single property pin with a popup +
// "Get Directions" button. Drop-in replacement for the previous
// MapLibre implementation, same props, same UX shell.
//
// APIProvider is mounted INSIDE this component (not at the app root) so
// the Google Maps JS API only downloads when a PropertyMap actually
// mounts. Combined with the LazyOnView wrap in PropertyDetail, that
// defers the ~270KB script + ~1.5s parse from page-load to scroll.
// The library deduplicates the underlying <script> tag, so multiple
// PropertyMap instances on one page share a single load.
const PropertyMap = ({ coordinates, title, className = "" }: PropertyMapProps) => {
  const [lng, lat] = coordinates;
  const position = { lat, lng };
  const [open, setOpen] = useState(false);
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || "";
  // Load-failure fallback + retry (flaky connections leave a dead blank box
  // otherwise, the script is fetched at runtime).
  const { key: retryKey, retry, onLoad, ready, retrying } = useMapRetryKey();

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div className={`rounded-card overflow-hidden border ${className}`}>
      <div className="h-64 w-full">
        <APIProvider key={retryKey} apiKey={apiKey} libraries={["marker"]} onLoad={onLoad}>
          <MapLoadGuard retry={retry} ready={ready} retrying={retrying}>
          <Map
            defaultCenter={position}
            defaultZoom={14}
            gestureHandling={inlineMapGestures()}
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            mapId="msth-property-map"
          >
            <AdvancedMarker position={position} onClick={() => setOpen(true)} />
            {open && (
              <InfoWindow position={position} onCloseClick={() => setOpen(false)}>
                <strong>{title}</strong>
              </InfoWindow>
            )}
          </Map>
          </MapLoadGuard>
        </APIProvider>
      </div>
      <div className="p-3 flex items-center justify-between bg-popover gap-3">
        <span className="text-xs text-muted-foreground truncate">{title}</span>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="rounded-full gap-1.5 text-xs"
        >
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open directions in Google Maps"
            onClick={() => track("directions_click")}
          >
            <Navigation className="h-3.5 w-3.5" />
            Get Directions
          </a>
        </Button>
      </div>
    </div>
  );
};

export default PropertyMap;
